import * as Sentry from '@sentry/react';
import { cookiesPermitted, setPreferWebXRCamera } from '@zappar/zappar-react-three-fiber';
import { createRoot } from 'react-dom/client';
import App from './components/App/App';
import { IComponentsById, IComponentType, IContentDoc, IRoot, ISceneComp, ITrackingTypes } from './components/r3f-components/component-data-structure';
import { IAnalyticsProvider, IAnalyticsEvent, IAnalyticsTrackingDict } from './components/r3f-components/component-data-structure/types/analytics';
import HintGraphicSvgUrl from './components/r3f-components/components/assets/MotionPrompt.svg';
import { showDefaultErrorPage } from './default-pages/error-page/error-page';
import { GoogleAnalyticsProvider } from './utils/analytics/googleAnalyticsProvider';
import { MsClarityAnalyticsProvider } from './utils/analytics/msClarityAnalyticsProvider';
import { IMode } from './zustand';
import { LmsClient, getLmsClient } from './lms-client';
import { isSafariWithAudioContextBug } from './components/r3f-components/utils/general';
import { intialiseWebxrSession } from './components/TrackerGroups/WorldXRTracker/webxr-utils';
import { TEST_SOURCE_FILE_NAME } from '../playwright/visual-regression/utils/constants';
import { StaticGeometryWorkerController } from './components/r3f-components/staticGeometryWorkerController/StaticGeometryWorkerController';
import { BVHWorkerController } from './components/r3f-components/BVHworkerController/BVHworkerController';
import { filestore } from './filestore';
import { isAppClip } from '@zappar/zappar';
import { Jute } from '.';
import { getTranslation, ILocale, ITranslationKey, translationMap } from './translations';

// import { GenerateMeshBVHWorker } from "three-mesh-bvh/src/workers/GenerateMeshBVHWorker.js";

////
// ALLOW STANDALONE BUILDS TO GET THE NECESSARY EXTERNAL FILES
/* eslint-disable no-var */
declare var Z_STANDALONE: string | undefined;
declare var __webpack_public_path__: string | undefined;
/* eslint-enable no-var */

if (typeof Z_STANDALONE !== 'undefined' && Z_STANDALONE) {
	if (document.currentScript) {
		const url = new URL((document.currentScript as HTMLScriptElement).src);
		let href = url.toString();
		if (url.pathname) {
			let pathParts = href.split('/');
			pathParts.pop();
			href = pathParts.join('/') + '/';
		}
		/* eslint-disable @typescript-eslint/no-unused-vars */
		__webpack_public_path__ = href;
		/* eslint-enable @typescript-eslint/no-unused-vars */
	}
}
///////
declare const JUTE_VERSION: string; // Set by webpack
const ROOT_ID = 'jute_root';

type IOnFirstCameraFrameCb = () => unknown;

export type IEnvironment = 'production' | 'development' | 'test';
interface IJuteInitInput {
	rootEl?: HTMLElement;
	launchButton?: HTMLElement;
	onLoaded?: () => unknown;
	onLoading?: () => unknown;
	onUnsupported?: () => unknown;
	onFirstCameraFrame?: () => unknown;
}
type IJuteInit = (args: IJuteInitInput) => void;
export class JuteManager {
	private _launchCbs: (() => any)[] = [];
	private _onSessionStartedCbs: ((session: XRSession) => any)[] = [];
	private _rootEl?: HTMLElement | HTMLDivElement | HTMLBodyElement;
	private _loadingStartDate?: Date;
	private _onReadyCbs: (() => any)[] = [];
	private _onTestEntityClickCbs: ((id: string) => any)[] = [];
	private _contentLoadedCbs: (() => any)[] = [];
	private _onLoading?: () => any;
	private _onUnsupported?: () => any;
	private _onTestTimeChange?: (t: number) => any;
	private _firstFrameVisible = false;
	private _onloadedBeforeFirstFrame = false;
	private _testTransitionTimeStepsMs?: number;
	private _environment: IEnvironment = 'production';
	private _version = JUTE_VERSION;
	private _versionSpan?: HTMLSpanElement;
	private _languageSelection?: HTMLSelectElement;
	private _analyticsProviderDict = {} as { -readonly [k in keyof typeof IAnalyticsProvider]: null | GoogleAnalyticsProvider | MsClarityAnalyticsProvider };
	private _onFirstCameraFrameCbs: IOnFirstCameraFrameCb[] = [];
	private _requiredPermissions = { camera: true, orientation: false, motion: false };
	private _grantedPermissions = { camera: false, orientation: false, motion: false };
	private _sceneTypes: ITrackingTypes[] = []
	
	public isDevRelease: boolean = false;
	public forcedLocale: ILocale | null = null;
	public lmsClient: LmsClient | undefined;
	public sceneTitleById = {} as { [sceneId: string]: string };
	public isLaunched = false;
	public isContentLoaded = false;
	public isReady = false;
	public isWebXREnabledExperience = false;
	public isWebXRImmersiveArSupported = false;
	public isMobileWebXRExperience = false;
	public isHeadsetWebXRExperience = false;
	public isAppClip = false; 
	public isMobileDevice = false;
	public isSafari = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome') && !navigator.userAgent.includes('Chromium');
	public isNativeHlsSupported = false;
	public isHeadset = false;
	public session?: XRSession | null;
	public isCookieConsentEnabled: boolean = false;
	public audioContext = new AudioContext();
	public audioStreamDestination = this.audioContext.createMediaStreamDestination();
	public staticGeometryWorkerController = new StaticGeometryWorkerController();
	public bvhWorkerController = new BVHWorkerController();

	constructor() {
		this.lmsClient = getLmsClient();
		if (typeof navigator.permissions !== 'undefined') {
			navigator.permissions.query({ name: 'camera' as PermissionName }).then((permission) => {
				permission.addEventListener('change', () => {
					this._grantedPermissions.camera = permission.state === 'granted';
				});
			});
			navigator.permissions.query({ name: 'gyroscope' as PermissionName }).then((permission) => {
				permission.addEventListener('change', () => {
					this._grantedPermissions.orientation = permission.state === 'granted';
				});
			});
			navigator.permissions.query({ name: 'accelerometer' as PermissionName }).then((permission) => {
				permission.addEventListener('change', () => {
					this._grantedPermissions.motion = permission.state === 'granted';
				});
			});
		}
		const video = document.createElement('video');
		this.isNativeHlsSupported = !!video.canPlayType('application/vnd.apple.mpegurl');
		// console.log('can play hls natively', !!video.canPlayType('application/vnd.apple.mpegurl'))
	}

	init: IJuteInit = async ({ rootEl, launchButton, onLoading, onLoaded, onFirstCameraFrame, onUnsupported }) => {
		this.isDevRelease = this._version.startsWith('dev/');

		if (this.isDevRelease) {
			console.log('DEV version:', this._version.replace('dev/', ''));
			this._versionSpan = document.createElement('span');
			this._versionSpan.innerText = this._version;
			this._versionSpan.style.position = 'absolute';
			this._versionSpan.style.top = '0px';
			this._versionSpan.style.right = '0px';
			this._versionSpan.style.color = 'red';
			this._versionSpan.style.zIndex = '9999999';
			this._versionSpan.style.fontSize = '24px';
			document.body.appendChild(this._versionSpan);

			this._languageSelection = document.createElement('select');

			this._languageSelection.addEventListener('change', (ev: Event) => {
				const selectedLocale = (ev.target as HTMLSelectElement)?.value as ILocale;
				this.forcedLocale = selectedLocale;
				if (launchButton) launchButton.textContent = getTranslation(ITranslationKey.launch)
			});

			Object.assign(this._languageSelection.style, {
				padding: '6px 10px',
				fontSize: '1.2rem',
				borderRadius: '8px',
				border: '2px solid #007AFF',
				cursor: 'pointer',
				minWidth: '100px',
				position: 'absolute',
				top: '40px',
				right: '10px',
				zIndex: '9999999'
			});

			const locales = Object.keys(translationMap) as ILocale[];

			locales.forEach(locale => {
				const option = document.createElement('option');
				option.value = locale;
				option.textContent = locale;
				this._languageSelection?.appendChild(option);
			})

			document.body.appendChild(this._languageSelection);
		}

		if (this.isQuest || this.isZapbox || this.isPico) {
			this.isHeadset = true;
		}

		this.isMobileDevice = this._isMobileDevice();

		this.isWebXREnabledExperience = (window as any).isWebXREnabledExperience ?? false;
		this._rootEl = rootEl;

		if (this.isIframe) {
			this._rootEl = document.body;
		} else {
			document.body.classList.add('hydrated'); // displays the launch screen UI
		}

		let contentUrl = `${window.location.pathname}content.json`;
		if (!window.location.pathname.endsWith('/')) {
			contentUrl = `${window.location.pathname}/content.json`;
			// After adding the full path to the content.json above, some URLs are still loading incorrectly when the window path doesn't end with a trailing slash
			window.history.replaceState(null, document.title, `${window.location.pathname}/`);
		}

		if (this._environment === 'test') contentUrl = TEST_SOURCE_FILE_NAME;

		const contentDoc = (await (await fetch(contentUrl)).json()) as IContentDoc;
		if (typeof (window as any).isWebXRExperience !== 'undefined') {
			this.isWebXREnabledExperience = (window as any).isWebXREnabledExperience;
		} else {
			this.isWebXREnabledExperience = contentDoc.device === 'headset' || !!contentDoc.isWebXREnabledExperience;
		}

		this.isWebXRImmersiveArSupported =  await this._checkWebXRImmersiveARSupport();

		const projectHasWorldTracking = this._checkProjectHasWorldTrackedScenes(contentDoc.componentsById, contentDoc.rootComponentId);
		this.isMobileWebXRExperience =  this.isWebXREnabledExperience && this.isMobileDevice && this.isWebXRImmersiveArSupported && projectHasWorldTracking;

		if (this.isMobileWebXRExperience) setPreferWebXRCamera(true);
		if (this._environment !== 'test') this._initSentry();
		if (typeof contentDoc === 'undefined') throw 'Content not loaded';

		this.isAppClip = isAppClip();

		if (this.isMobileWebXRExperience || this.isAppClip) this._setViewportFitCover();

		const rootId = (contentDoc?.rootComponentId ?? '') as keyof typeof contentDoc;
		this._sceneTypes = (contentDoc?.[rootId] as IRoot)?.children?.map(id => (contentDoc?.[id as keyof typeof contentDoc] as ISceneComp)?.trackingType)
		
		this._initAnalytics(contentDoc.analyticsTracking);
		this.logEventToAnalytics({
			category: 'General',
			name: 'First load',
			label: 'First load',
			value: 1,
		});

		if (!this._rootEl && !this.isIframe) {
			this._rootEl = document.createElement('div') as HTMLDivElement;
			this._rootEl.style.visibility = 'hidden';
			this._rootEl.style.height = '100%';
			this._rootEl.style.width = '100%';
			this._rootEl.style.position = 'absolute';
			this._rootEl.style.top = '0px';
			this._rootEl.style.left = '0px';
			this._rootEl.style.touchAction = 'none';
			this._rootEl.id = ROOT_ID;
			document.body.append(this._rootEl);
		}

		this._onUnsupported = onUnsupported;
		if (onLoaded) this._onReadyCbs.push(onLoaded);
		if (onFirstCameraFrame) this.onFirstCameraFrame(onFirstCameraFrame);
		filestore.load(contentDoc.mediaFilesById);

		const root = createRoot(this._rootEl!);

		root.render(
			<Sentry.ErrorBoundary onError={this.error}>
				<App contentDoc={contentDoc} />
			</Sentry.ErrorBoundary>
		);

		if (this.isIframe) {
			this.isCookieConsentEnabled = false;
			this.updateCookieConsent(false);
			onLoading?.();
			this._launch();
		} else {
			// Check if there is a 360 scene in the contentDoc
			for (const compId in contentDoc.componentsById) {
				const comp = contentDoc.componentsById[compId];
				if (comp.type === IComponentType.Scene && comp.trackingType === ITrackingTypes.content360) {
					// Only 360 scenes require orientation and motion access
					this._requiredPermissions.orientation = true;
					this._requiredPermissions.motion = true;
					break;
				}
			}
			launchButton?.addEventListener?.('click', () => {
				this._launchProject(onLoading);
			});
		}

		document.body.classList.add('hydrated');
		document.dispatchEvent(new CustomEvent('jute:ready', { detail: this }));
	};

	private _launchProject = async (loadingCb?: (() => any) | undefined) => {
		loadingCb?.();
		this._launch();
		if (!isSafariWithAudioContextBug()) this.audioContext.resume();

		if (!this.isWebXREnabledExperience || !this.isHeadset) return;

		const optionalFeatures = ['local-floor', 'bounded-floor'];
		const requiredFeatures = [];
		if (!this.isPico) requiredFeatures.push('hit-test');
		this.session = await intialiseWebxrSession({ requiredFeatures, optionalFeatures });
		if (this.session) this._onSessionStarted(this.session);
	};

	public updateCookieConsent = (consent: boolean): void => {
		// loop over the IAnalyticsProvider enum and updateCookieConsent on all
		cookiesPermitted(consent);
		this._analyticsProviderDict[IAnalyticsProvider.googleAnalytics]?.updateCookieConsent(consent);
		this._analyticsProviderDict[IAnalyticsProvider.microsoftClarity]?.updateCookieConsent(consent);
	};

	private _initAnalytics = (analyticsTracking?: IAnalyticsTrackingDict) => {
		const gTagIds = analyticsTracking?.[IAnalyticsProvider.googleAnalytics];
		const msClarityTagIds = analyticsTracking?.[IAnalyticsProvider.microsoftClarity];
		this.isCookieConsentEnabled = !!gTagIds || !!msClarityTagIds;

		if (gTagIds) this._analyticsProviderDict[IAnalyticsProvider.googleAnalytics] = new GoogleAnalyticsProvider(gTagIds);
		if (msClarityTagIds) this._analyticsProviderDict[IAnalyticsProvider.microsoftClarity] = new MsClarityAnalyticsProvider(msClarityTagIds);
	};

	public logEventToAnalytics = (event: IAnalyticsEvent): void => {
		// console.log('Log event to analytics, ', event);
		// loop over the IAnalyticsProvider enum and sendEvent on all
		this._analyticsProviderDict[IAnalyticsProvider.googleAnalytics]?.sendEvent(event);
		this._analyticsProviderDict[IAnalyticsProvider.microsoftClarity]?.sendEvent(event);
	};

	private _initSentry = (): void => {
		Sentry.init({
			dsn: 'https://1636ccf870c64cbbac6a9491a55c0321@o160191.ingest.sentry.io/5689090',
			environment: this._environment,
			release: this._version,
			integrations: [Sentry.httpClientIntegration()],
			tracesSampleRate: 0.9,
			ignoreErrors: ['AbortError: The operation was aborted.', 'TypeError: cancelled'],
			tracesSampler: ({ name }) => {
				// Reduce sample rate to 25%
				if (name.includes('device-orientation')) return 0.25;
				// Otherwise, inherit the sample sampling decision of the incoming trace, or use a fallback sampling rate.
				return 0.9;
			},
		});
	};

	private _requestPermissions = () => {
		return new Promise<void>((res) => {
			let hasCheckedCamera = !this._requiredPermissions.camera;
			let hasCheckedOrientation = !this._requiredPermissions.orientation;
			let hasCheckedMotion = !this._requiredPermissions.motion;

			const permissionChecked = () => {
				if (hasCheckedCamera && hasCheckedOrientation && hasCheckedMotion) res();
			};

			if (this._requiredPermissions.camera && !this._grantedPermissions.camera) {
				// Request camera permission
				navigator.mediaDevices.getUserMedia({ video: true }).then((stream) => {
					stream.getTracks().forEach((track) => track.stop()); // Stop the stream immediately
					this._grantedPermissions.camera = true;
					hasCheckedCamera = true;
					permissionChecked();
				});
			}

			if (this._requiredPermissions.orientation && !this._grantedPermissions.orientation) {
				// Request device orientation permission (iOS 13+)
				if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
					(DeviceOrientationEvent as any).requestPermission().then((orientationPermission: any) => {
						this._grantedPermissions.orientation = orientationPermission === 'granted';
						hasCheckedOrientation = true;
						permissionChecked();
					});
				} else {
					// no permissions required
					hasCheckedOrientation = true;
					permissionChecked();
				}
			}

			if (this._requiredPermissions.motion && !this._grantedPermissions.motion) {
				// Request device motion permission (iOS 13+)
				if (typeof (DeviceMotionEvent as any).requestPermission === 'function') {
					(DeviceMotionEvent as any).requestPermission().then((motionPermission: any) => {
						this._grantedPermissions.motion = motionPermission === 'granted';
						hasCheckedMotion = true;
						permissionChecked();
					});
				} else {
					// no permissions required
					hasCheckedMotion = true;
					permissionChecked();
				}
			}
		});
	};

	private _arePermissionsGranted = () => {
		return (
			this._grantedPermissions.camera === this._requiredPermissions.camera &&
			this._grantedPermissions.orientation === this._requiredPermissions.orientation &&
			this._grantedPermissions.motion === this._requiredPermissions.motion
		);
	};

	private _launch = (): void => {
		this._requestPermissions().then(() => {
			// console.log('Required permissions:', JSON.stringify(this._requiredPermissions))
			// console.log('Granted permissions:', JSON.stringify(this._grantedPermissions))
			for (let i = 0; i < this._launchCbs.length; i++) {
				this._launchCbs[i]?.();
			}
			if (this.isDevRelease && this._versionSpan) {
				if (this._versionSpan) document.body.removeChild(this._versionSpan)
				if (this._languageSelection) document.body.removeChild(this._languageSelection)
			};

			this.isLaunched = true;
			this.lmsClient?.initialize();
			this._loadingStartDate = new Date();
			
			setTimeout(() => {
				if (!this._firstFrameVisible) {
					Sentry.withScope((scope) => {
						scope.setExtra('jute state', { 
							firstFrameVisible: this._firstFrameVisible, 
							contentLoaded: this.isContentLoaded, 
							isAppClip: Jute.isAppClip, 
							isMobileWebXRExperience: Jute.isMobileWebXRExperience,
							isHeadset: Jute.isHeadset,
							isIframe: Jute.isIframe,
							sceneTypes: this._sceneTypes
						});
						Sentry.captureMessage('First camera frame not available 5s after project launch');
					})};
			}, 5000)
		});

		window.addEventListener('beforeunload', () => {
			if (this.isReady) return;
			if (!this._arePermissionsGranted()) return; // Not all permissions are granted, so we don't log the error
			const timeInSecSinceLoadingStart = (new Date().getTime() - this._loadingStartDate!.getTime()) / 1000;
			Sentry.withScope((scope) => {
				scope.setExtra('loading data', { timeInSecSinceLoadingStart, firstFrameVisible: this._firstFrameVisible, contentLoaded: this.isContentLoaded, permissionsGranted: this._arePermissionsGranted() });
				Sentry.captureMessage('Jute was closed before content loaded');
			});
		});
	};

	onLaunch = (cb: () => any): void => {
		this._launchCbs.push(cb);
	};

	private _onSessionStarted = (session: XRSession): void => {
		for (let i = 0; i < this._onSessionStartedCbs.length; i++) {
			this._onSessionStartedCbs[i]?.(session);
		}
	};

	onSessionStarted = (cb: (session: XRSession) => any): void => {
		this._onSessionStartedCbs.push(cb);
	};

	private _ready = (): void => {
		console.log('jute manager: ready to show experience');
		(this._rootEl as HTMLElement).style.visibility = 'visible';
		const meta = document.createElement('meta');
		meta.name = 'viewport';
		meta.content = 'width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=no';
		document.getElementsByTagName('head')[0].appendChild(meta);

		this.isReady = true;

		for (let i = 0; i < this._onReadyCbs.length; i++) {
			this._onReadyCbs[i]?.();
		}
		if (!this._loadingStartDate) this._loadingStartDate = new Date();

		const timeInSecSinceLoadingStart = (new Date().getTime() - this._loadingStartDate!.getTime()) / 1000;

		if (timeInSecSinceLoadingStart > 5 && !this._firstFrameVisible) {
			Sentry.withScope((scope) => {
				scope.setExtra('loading data', { timeInSecSinceLoadingStart });
				Sentry.captureMessage('Camera not visible 5s to load content');
			});
		}

		if (timeInSecSinceLoadingStart > 20) {
			Sentry.withScope((scope) => {
				scope.setExtra('loading data', { timeInSecSinceLoadingStart });
				Sentry.captureMessage('Jute required longer than 20s to load content');
			});
		}
	};

	onReady = (cb: () => any): void => {
		this._onReadyCbs.push(cb);
	};

	private _contentLoaded = (): void => {
		this.isContentLoaded = true;
		// console.log('jute manager: content loaded');
		for (let i = 0; i < this._contentLoadedCbs.length; i++) {
			this._contentLoadedCbs[i]();
		}
	};

	onContentLoaded = (cb: () => any): void => {
		this._contentLoadedCbs.push(cb);
	};

	unsupported = (): void => {
		this._onUnsupported?.();
	};

	loading = (): void => {
		this._onLoading?.();
	};

	loaded = (): void => {
		this._contentLoaded();
		if (this._firstFrameVisible) {
			this._ready?.();
		} else {
			this._onloadedBeforeFirstFrame = true;
		}
	};

	testTimeChange = (t: number): void => {
		this._onTestTimeChange?.(t);
	};

	onTestTimeChange = (cb: (t: number) => any): void => {
		this._onTestTimeChange = cb;
	};

	testClickEntity = (id: string): void => {
		for (let i = 0; i < this._onTestEntityClickCbs.length; i++) {
			this._onTestEntityClickCbs[i](id);
		}
	};

	onTestClickEntity = (cb: (id: string) => any): void => {
		this._onTestEntityClickCbs.push(cb);
	};

	firstCameraFrame = (): void => {
		console.log('jute manager: first camera frame');
		this._firstFrameVisible = true;
		this._onFirstCameraFrameCbs.forEach((fn) => fn());
		if (this._onloadedBeforeFirstFrame || this._environment === 'test') {
			this._ready?.();
			// (this._rootEl as HTMLElement).style.visibility = 'visible';
		}
	};

	error = (): void => {
		showDefaultErrorPage();
	};

	public onFirstCameraFrame = (cb: IOnFirstCameraFrameCb) => {
		this._onFirstCameraFrameCbs.push(cb);
	};

	// This method is called from React
	showWebembedHintGraphic = (show: boolean, _document: Document): void => {
		const existingContainer = _document.getElementById('hintGraphicContainer');
		if (!show) {
			if (existingContainer) existingContainer.style.visibility = 'hidden';
			return;
		}
		if (show && existingContainer) {
			existingContainer.style.visibility = 'visible';
			return;
		}
		const newContainer = _document.createElement('div');
		newContainer.id = 'hintGraphicContainer';
		newContainer.style.zIndex = '999999';
		newContainer.style.position = 'absolute';
		newContainer.style.left = '0';
		newContainer.style.right = '0';
		newContainer.style.height = '100%';
		newContainer.style.width = '100%';
		newContainer.style.display = 'flex';
		newContainer.style.justifyContent = 'center';
		newContainer.style.alignItems = 'center';
		newContainer.style.pointerEvents = 'none';
		const img = _document.createElement('img');
		img.src = HintGraphicSvgUrl;
		img.style.zIndex = '99';
		img.style.width = '60px';
		img.style.height = '50px';
		newContainer.appendChild(img);
		document.body.appendChild(newContainer);
	};

	private _isMobileDevice = (): boolean => {
		// Only works for non-iOS devices
		if ('userAgentData' in navigator) {
			const uaData = (navigator as any).userAgentData;
			if (typeof uaData.mobile === 'boolean') {
				return uaData.mobile;
			}
		}

		// ios devices where browsers do not support userAgentData
		const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
		const isMobileUA = /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua.toLowerCase());

		// for tablets in desktop mode
		const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

		// safeguard against false positive results for android based headset browsers
		const isHeadsetUA = /oculus|quest|vive|rift|hololens|zapbox|windows mixed reality/i.test(ua.toLowerCase());

		return (isMobileUA || isTouchDevice) && !isHeadsetUA;
	};

	private _checkWebXRImmersiveARSupport = async () => {
		if (!('xr' in navigator)) {
			console.log('WebXR API not found.');
			return false;
		}
		try {
			const isImmersiveArSupported = !!(await navigator?.xr?.isSessionSupported('immersive-ar'));
			// if (isArSupported) console.log('Immersive AR (Augmented Reality) is supported!');
			// else console.log('Immersive AR not supported on this device/hardware.');
			return isImmersiveArSupported;
		} catch (error) {
			// console.error('Error checking session support:', error);
			return false;
		}
	};

	private _checkProjectHasWorldTrackedScenes = (componentsById: IComponentsById, rootId: string): boolean => {
		const root = componentsById?.[rootId || ''] as IRoot | undefined;
		const numWorldTrackedScenes = root?.children?.filter(id => (componentsById?.[id] as ISceneComp).trackingType === ITrackingTypes.world)?.length;
		return (numWorldTrackedScenes ?? 0) > 0;
	}

	private _setViewportFitCover = (): void => {
		// Try to find existing viewport meta tag
		let viewport: HTMLMetaElement | null = document.querySelector('meta[name="viewport"]');
	  
		// Create one if not found
		if (!viewport) {
		  viewport = document.createElement('meta');
		  viewport.name = 'viewport';
		  document.head.appendChild(viewport);
		}
	  
		// Get existing content safely
		let content: string = viewport.getAttribute('content') || '';
	  
		// Remove any existing viewport-fit declarations
		content = content
		  .split(',')
		  .map(s => s.trim())
		  .filter(s => !s.startsWith('viewport-fit='))
		  .join(', ');
	  
		// Add viewport-fit=cover at the end
		if (content) content += ', ';
		content += 'viewport-fit=cover';
	  
		// Apply the new content string
		viewport.setAttribute('content', content);
	};

	get isIframe(): boolean {
		const params = new URL(window.location.href).searchParams;
		const isEmbeded = params.get('mode') === IMode.nonar;
		const isIframe = () => {
			try {
				return window.self !== window.top;
			} catch (e) {
				return true;
			}
		};

		return isEmbeded || isIframe();
	}

	get isZapbox(): boolean {
		return navigator.userAgent.includes('Zapbox');
	}

	get isPico(): boolean {
		return navigator.userAgent.includes('PICO') || navigator.userAgent.includes('Pico') || navigator.userAgent.includes('PicoBrowser');
	}

	get isQuest(): boolean {
		return navigator.userAgent.includes('Quest');
	}

	get version(): string {
		return this._version || '';
	}

	set version(value: string) {
		this._version = value;
	}

	get environment(): IEnvironment {
		return this._environment;
	}

	set environment(env: IEnvironment) {
		this._environment = env;
	}

	get testTransitionTimeStepsMs(): number {
		return this._testTransitionTimeStepsMs || 10;
	}

	set testTransitionTimeStepsMs(value: number) {
		this._testTransitionTimeStepsMs = value;
	}
}
